import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Search, Shield, Book, FileText, Share2, Lock, Unlock } from 'lucide-react';

const SharedKnowledgeTab = ({ knowledgeItems, searchTerm, setSearchTerm, toggleShareToPortal, savingShare }) => {
  const filtered = knowledgeItems.filter(k => {
    const name = (k.name || k.title || '').toLowerCase();
    const type = (k.credential_type || k.category || k.record_type || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return !term || name.includes(term) || type.includes(term);
  });

  const credentials = filtered.filter(k => k._type === 'credential');
  const resources = filtered.filter(k => k._type === 'resource');
  const records = filtered.filter(k => k._type === 'record');

  const renderItem = (item, icon, iconColor) => {
    const Icon = icon;
    return (
      <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3]/40 transition-colors" data-testid={`share-item-${item.id}`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Icon className={`h-4 w-4 ${iconColor} shrink-0`} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#1A1D21] truncate">{item.name || item.title}</p>
            <p className="text-[10px] text-[#A8B1B9] capitalize">{(item.credential_type || item.category || item.record_type || '').replace('_', ' ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {item._shared ? <Unlock className="h-3.5 w-3.5 text-green-600" /> : <Lock className="h-3.5 w-3.5 text-[#A8B1B9]" />}
          <Switch checked={item._shared} onCheckedChange={() => toggleShareToPortal(item)} disabled={savingShare === item.id} data-testid={`share-toggle-${item.id}`} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search knowledge items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-knowledge-hub" />
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-[#A8B1B9]">{knowledgeItems.filter(k => k._shared).length} of {knowledgeItems.length} items shared</p>
        </div>
      </div>
      {knowledgeItems.length === 0 ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
          <Share2 className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
          <h3 className="text-base font-medium text-[#1A1D21] mb-1">No items in Library</h3>
          <p className="text-sm text-[#5A6570]">Add credentials, resources, or records first</p>
        </CardContent></Card>
      ) : filtered.length === 0 && searchTerm ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-8 text-center">
          <Search className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
          <p className="text-sm text-[#5A6570]">No items match "{searchTerm}"</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {credentials.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#5A6570] uppercase tracking-wide mb-2 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" />Corporate Credentials</h3>
              <div className="space-y-1.5">{credentials.map(item => renderItem(item, Shield, 'text-[#00A3A3]'))}</div>
            </div>
          )}
          {resources.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-[#5A6570] uppercase tracking-wide mb-2 flex items-center gap-1.5"><Book className="h-3.5 w-3.5" />Resources</h3>
              <div className="space-y-1.5">{resources.map(item => renderItem(item, Book, 'text-[#6366f1]'))}</div>
            </div>
          )}
          {records.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-[#5A6570] uppercase tracking-wide mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Records</h3>
              <div className="space-y-1.5">{records.map(item => renderItem(item, FileText, 'text-[#F58A26]'))}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedKnowledgeTab;
